import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { authService } from '../../lib/auth-service';
import {
  User, Mail, Phone, Shield, Lock, Bell, Eye, Camera,
  Save, ArrowLeft, AlertTriangle, Settings as SettingsIcon, LogOut
} from 'lucide-react';

export default function AccountSettings() {
  const { currentUser, refreshFamilyData } = useFamilyContext();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    timeZone: 'America/New_York',
    bio: '',
    avatar: ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: false,
    familyUpdates: true,
    weeklySummary: true,
    marketing: false,
    twoFactorAuth: false,
    loginNotifications: true,
    sessionManagement: false,
    quietHours: {
      start: '22:00',
      end: '08:00'
    }
  });
  
  const [privacy, setPrivacy] = useState({
    profileVisibility: true,
    activitySharing: true,
    analyticsData: false,
    dataExport: true
  });

  // Initialize form data
  useEffect(() => {
    if (currentUser) {
      const names = currentUser.name?.split(' ') || ['', ''];
      setProfileForm({
        firstName: names[0] || '',
        lastName: names.slice(1).join(' ') || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        dateOfBirth: currentUser.dateOfBirth || '',
        timeZone: currentUser.timeZone || 'America/New_York',
        bio: currentUser.bio || '',
        avatar: currentUser.avatar || ''
      });
    }
  }, [currentUser]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileForm.firstName.trim()) {
      setError('First name is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const fullName = `${profileForm.firstName} ${profileForm.lastName}`.trim();
      
      // Update family member record
      const { error: memberError } = await supabase
        .from('family_members')
        .update({
          name: fullName,
          email: profileForm.email || null,
          phone: profileForm.phone || null,
          date_of_birth: profileForm.dateOfBirth || null,
          avatar: profileForm.avatar,
          bio: profileForm.bio || null,
          time_zone: profileForm.timeZone
        })
        .eq('id', currentUser.id);

      if (memberError) throw memberError;

      // Update auth email if changed
      if (profileForm.email !== currentUser.email) {
        const { error: authError } = await supabase.auth.updateUser({
          email: profileForm.email
        });
        if (authError) throw authError;
      }

      setSuccess('Profile updated successfully!');
      await refreshFamilyData();
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (currentUser?.originalParent) {
        // Parent is managing child's account - use server function
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('You must be logged in to reset passwords');
        }

        const { data, error } = await supabase.functions.invoke('reset-child-password', {
          body: {
            childId: currentUser.id,
            newPassword: passwordForm.newPassword
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (error) {
          console.error('Function error:', error);
          throw error;
        }

        if (data?.error) {
          console.error('Function returned error:', data.error);
          throw new Error(data.error);
        }
        
        setSuccess(`Password updated successfully for ${currentUser.name}!`);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        // User is updating their own password
        const { error } = await supabase.auth.updateUser({
          password: passwordForm.newPassword
        });
        if (error) throw error;
        
        setSuccess('Password updated successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
      
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    setUploadingPhoto(true);
    setError('');

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('family-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('family-assets')
        .getPublicUrl(filePath);

      setProfileForm(prev => ({ ...prev, avatar: publicUrl }));
      
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 'weak', width: 0 };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    
    if (score >= 4) return { strength: 'strong', width: 100 };
    if (score >= 2) return { strength: 'medium', width: 66 };
    return { strength: 'weak', width: 33 };
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: Eye }
  ];

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await authService.signOut();
      // Force a page reload to ensure clean auth state
      window.location.href = '/';
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto py-8 px-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 mb-8">
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-stone-600" />
                </button>
                <div className="border-l border-stone-200 h-8"></div>
                <SettingsIcon className="w-6 h-6 text-teal-600" />
                <div>
                  <h1 className="text-3xl font-bold text-stone-900">Account Settings</h1>
                  <p className="mt-1 text-stone-600">
                    {currentUser?.originalParent 
                      ? `Managing ${currentUser?.name}'s account preferences and security`
                      : 'Manage your personal account preferences and security'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800">
            {success}
          </div>
        )}

        {/* Parent managing child notice */}
        {currentUser?.originalParent && (
          <div className="mb-6 p-4 bg-azure-50 border border-azure-200 rounded-xl text-azure-800">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold">Parent Management Mode</h4>
                <p className="text-sm">
                  You're managing {currentUser.name}'s account as a parent. You can update their profile information, 
                  reset their password, and adjust account settings to help them.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
              <div className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-teal-100 text-teal-700'
                          : 'text-stone-600 hover:bg-stone-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
                {/* Profile Hero */}
                <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 mb-8 text-white">
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-white/20 border-4 border-white/30 flex items-center justify-center">
                        {profileForm.avatar && profileForm.avatar.startsWith('http') ? (
                          <img 
                            src={profileForm.avatar} 
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-3xl">{profileForm.avatar || '👤'}</span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = e.target.files[0];
                            if (file) handlePhotoUpload(file);
                          };
                          input.click();
                        }}
                        disabled={uploadingPhoto}
                        className="absolute -bottom-1 -right-1 bg-violet-600 border-3 border-white rounded-full w-8 h-8 flex items-center justify-center text-white hover:bg-violet-700 transition-colors"
                      >
                        {uploadingPhoto ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <Camera className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{currentUser?.name}</h2>
                      <p className="opacity-90">
                        {currentUser?.isParent ? 'Family Owner • Parent' : 'Child'}
                        {currentUser?.originalParent && (
                          <span className="ml-2 text-sm bg-white/20 px-2 py-1 rounded">
                            (Managing as Parent)
                          </span>
                        )}
                      </p>
                      <div className="mt-2 bg-white/20 px-3 py-1 rounded-full text-sm inline-block">
                        ✅ Account Verified
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleProfileSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.firstName}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.lastName}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                      <p className="mt-1 text-sm text-stone-600">
                        We'll send important account updates to this email
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={profileForm.dateOfBirth}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Time Zone
                      </label>
                      <select
                        value={profileForm.timeZone}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, timeZone: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      >
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/New_York">Eastern Time (ET)</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Bio (Optional)
                      </label>
                      <textarea
                        value={profileForm.bio}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                        placeholder="Tell us a bit about yourself..."
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <Lock className="w-4 h-4 text-red-600" />
                  </div>
                  Password & Security
                </h2>

                {currentUser?.originalParent && (
                  <div className="mb-6 p-4 bg-azure-50 border border-azure-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-azure-800">Direct Password Reset for {currentUser.name}</h4>
                        <p className="text-sm text-azure-700">
                          You can set a new password for {currentUser.name} directly below. 
                          No email confirmation required - the new password will be active immediately.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!currentUser?.originalParent && (
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          required
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                      {passwordForm.newPassword && (
                        <div className="mt-2">
                          <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-300 ${
                                passwordStrength.strength === 'strong' ? 'bg-emerald-500' :
                                passwordStrength.strength === 'medium' ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${passwordStrength.width}%` }}
                            />
                          </div>
                          <p className={`text-sm mt-1 ${
                            passwordStrength.strength === 'strong' ? 'text-emerald-600' :
                            passwordStrength.strength === 'medium' ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {passwordStrength.strength.charAt(0).toUpperCase() + passwordStrength.strength.slice(1)} password
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Shield className="w-4 h-4" />
                      )}
                      {currentUser?.originalParent ? `Set New Password for ${currentUser.name}` : 'Update Password'}
                    </button>
                  </div>
                </form>

                {/* Security Options */}
                <div className="mt-8 pt-8 border-t border-stone-200">
                  <h3 className="text-lg font-semibold mb-4">Security Options</h3>
                  <div className="space-y-4">
                    {[
                      { key: 'twoFactorAuth', title: 'Two-Factor Authentication', desc: 'Add an extra layer of security to your account' },
                      { key: 'loginNotifications', title: 'Login Notifications', desc: 'Get notified when someone signs into your account' },
                      { key: 'sessionManagement', title: 'Session Management', desc: 'Automatically sign out inactive sessions' }
                    ].map((option) => (
                      <div key={option.key} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-stone-900">{option.title}</h4>
                          <p className="text-sm text-stone-600">{option.desc}</p>
                        </div>
                        <button
                          onClick={() => setNotifications(prev => ({ 
                            ...prev, 
                            [option.key]: !prev[option.key] 
                          }))}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            notifications[option.key] ? 'bg-teal-500' : 'bg-stone-300'
                          }`}
                        >
                          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications[option.key] ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Logout Section */}
                <div className="mt-8 pt-8 border-t border-stone-200">
                  <h3 className="text-lg font-semibold mb-4">Sign Out</h3>
                  <div className="bg-stone-50 rounded-lg p-6">
                    <p className="text-stone-700 mb-4">
                      Sign out of your account on this device. You'll need to sign in again to access your account.
                    </p>
                    <button
                      onClick={handleLogout}
                      disabled={loading}
                      className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <LogOut className="w-4 h-4" />
                      )}
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Bell className="w-4 h-4 text-yellow-600" />
                  </div>
                  Notification Preferences
                </h2>

                <div className="space-y-4">
                  {[
                    { key: 'emailNotifications', title: 'Email Notifications', desc: 'Receive updates and alerts via email' },
                    { key: 'smsNotifications', title: 'SMS Notifications', desc: 'Receive important alerts via text message' },
                    { key: 'pushNotifications', title: 'Push Notifications', desc: 'Receive notifications in your browser or app' },
                    { key: 'familyUpdates', title: 'Family Activity Updates', desc: 'Get notified about your family\'s activities and progress' },
                    { key: 'weeklySummary', title: 'Weekly Summary', desc: 'Receive a weekly digest of family activities' },
                    { key: 'marketing', title: 'Marketing Communications', desc: 'Receive updates about new features and tips' }
                  ].map((option) => (
                    <div key={option.key} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-stone-900">{option.title}</h4>
                        <p className="text-sm text-stone-600">{option.desc}</p>
                      </div>
                      <button
                        onClick={() => setNotifications(prev => ({ 
                          ...prev, 
                          [option.key]: !prev[option.key] 
                        }))}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications[option.key] ? 'bg-teal-500' : 'bg-stone-300'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          notifications[option.key] ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Quiet Hours */}
                <div className="mt-8 pt-8 border-t border-stone-200">
                  <h3 className="text-lg font-semibold mb-4">Quiet Hours</h3>
                  <div className="flex items-center gap-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">From</label>
                      <input
                        type="time"
                        value={notifications.quietHours.start}
                        onChange={(e) => setNotifications(prev => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, start: e.target.value }
                        }))}
                        className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <span className="mt-6">to</span>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">To</label>
                      <input
                        type="time"
                        value={notifications.quietHours.end}
                        onChange={(e) => setNotifications(prev => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, end: e.target.value }
                        }))}
                        className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-stone-600 mt-2">
                    No notifications will be sent during these hours
                  </p>
                </div>
              </div>
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Eye className="w-4 h-4 text-violet-600" />
                  </div>
                  Privacy & Data
                </h2>

                <div className="space-y-4">
                  {[
                    { key: 'profileVisibility', title: 'Profile Visibility', desc: 'Allow other family members to see your profile details' },
                    { key: 'activitySharing', title: 'Activity Sharing', desc: 'Share your activity status with family members' },
                    { key: 'analyticsData', title: 'Analytics & Usage Data', desc: 'Help improve the app by sharing anonymous usage data' },
                    { key: 'dataExport', title: 'Data Export', desc: 'Download a copy of your data at any time' }
                  ].map((option) => (
                    <div key={option.key} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-stone-900">{option.title}</h4>
                        <p className="text-sm text-stone-600">{option.desc}</p>
                      </div>
                      <button
                        onClick={() => setPrivacy(prev => ({ 
                          ...prev, 
                          [option.key]: !prev[option.key] 
                        }))}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          privacy[option.key] ? 'bg-teal-500' : 'bg-stone-300'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          privacy[option.key] ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Danger Zone - Show different options based on user type */}
                <div className="mt-8 pt-8 border-t border-stone-200">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-red-800 mb-2">Danger Zone</h3>
                        {currentUser?.originalParent ? (
                          // When parent is managing child's account
                          <div>
                            <p className="text-red-700 text-sm mb-4">
                              Child account management options. These actions require careful consideration.
                            </p>
                            <div className="space-y-3">
                              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 text-sm">
                                Remove Child from Family
                              </button>
                              <p className="text-red-600 text-xs">
                                Note: To delete a child's account entirely, please contact support.
                              </p>
                            </div>
                          </div>
                        ) : (
                          // When managing own account
                          <div>
                            <p className="text-red-700 text-sm mb-4">
                              These actions are permanent and cannot be undone. Please proceed with caution.
                            </p>
                            <div className="space-y-3">
                              <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm">
                                Delete Account
                              </button>
                              {currentUser?.isParent && (
                                <button className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 text-sm ml-3">
                                  Transfer Family Ownership
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 